import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

export default function TestimonialsManager() {
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({});
  const queryClient = useQueryClient();

  const { data: testimonials = [] } = useQuery({
    queryKey: ['testimonials'],
    queryFn: () => base44.entities.Testimonial.list(),
  });

  const createMutation = useMutation({
    mutationFn: (data) => base44.entities.Testimonial.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['testimonials'] });
      setEditing(null);
      setFormData({});
      toast.success('Testimonianza creata');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.Testimonial.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['testimonials'] });
      setEditing(null);
      setFormData({});
      toast.success('Testimonianza aggiornata');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => base44.entities.Testimonial.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['testimonials'] });
      toast.success('Testimonianza eliminata');
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (editing === 'new') {
      createMutation.mutate(formData);
    } else {
      updateMutation.mutate({ id: editing, data: formData });
    }
  };

  if (editing) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{editing === 'new' ? 'Nuova Testimonianza' : 'Modifica Testimonianza'}</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Citazione *</Label>
              <Textarea
                value={formData.quote || ''}
                onChange={(e) => setFormData({ ...formData, quote: e.target.value })}
                rows={3}
                required
              />
            </div>
            <div>
              <Label>Fonte / Pubblicazione *</Label>
              <Input
                value={formData.source || ''}
                onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                required
              />
            </div>
            <div>
              <Label>Paese</Label>
              <Input
                value={formData.country || ''}
                onChange={(e) => setFormData({ ...formData, country: e.target.value })}
              />
            </div>
            <div className="flex gap-2">
              <Button type="submit">Salva</Button>
              <Button type="button" variant="outline" onClick={() => setEditing(null)}>
                Annulla
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-serif">Testimonianze Press</h2>
        <Button onClick={() => { setEditing('new'); setFormData({ quote: '', source: '', country: '' }); }}>
          <Plus className="w-4 h-4 mr-2" /> Nuova Testimonianza
        </Button>
      </div>

      <div className="grid gap-4">
        {testimonials.map((testimonial) => (
          <Card key={testimonial.id}>
            <CardContent className="p-4">
              <div className="flex justify-between gap-4">
                <div className="flex-1">
                  <p className="text-sm italic mb-2">"{testimonial.quote}"</p>
                  <p className="text-xs text-[#7a7a7a]">
                    — {testimonial.source} {testimonial.country && `(${testimonial.country})`}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="icon" onClick={() => { setEditing(testimonial.id); setFormData(testimonial); }}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="destructive"
                    size="icon"
                    onClick={() => {
                      if (confirm('Eliminare questa testimonianza?')) {
                        deleteMutation.mutate(testimonial.id);
                      }
                    }}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}